import { normalizeDepartmentCategories, type Department } from '../lib/departments';
import { DEFAULT_COMPLIANCE_CATEGORIES, type ComplianceCategory } from '../lib/complianceCategories';

export const BASE_COMPLIANCE_CARD_TITLES = [
  'เข็มตำ/ของมีคมบาด',
  'นิเทศระบบยา',
  'SIREN bundle',
  'บันทึกทางการพยาบาล',
  'นิเทศทางการพยาบาล',
];

export const computeAllowedCategories = (
  departments: Department[],
  isWildcardScope: boolean,
  isAdminScope: boolean,
  allCategories: ComplianceCategory[],
  scopedDepartmentIds: string[] = [],
  teamIds: string[] = [],
) => {
  const activeCategories = allCategories.filter((c) => c.active !== false);
  const activeCategoryMap = new Map(activeCategories.map((c) => [c.id, c]));
  const nursingTitle = activeCategoryMap.get('nurse_notes')?.title ?? 'บันทึกทางการพยาบาล';
  const medErrorTitle = activeCategoryMap.get('med_error')?.title ?? 'นิเทศระบบยา';
  const humanizeId = (id: string) =>
    id
      .split(/[-_]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const withForcedHubTitles = (titles: string[]) => {
    const next = [...titles];
    if (!next.includes(nursingTitle)) next.push(nursingTitle);
    if (activeCategoryMap.has('med_error') && !next.includes(medErrorTitle)) next.push(medErrorTitle);
    return next;
  };

  if (isAdminScope) {
    return activeCategories.map((c) => c.title);
  }

  const departmentCategories = departments
    .flatMap((d) => normalizeDepartmentCategories(d, activeCategories))
    .map((id) => activeCategoryMap.get(id)?.title ?? humanizeId(id))
    .filter((title): title is string => !!title);

  const unrestrictedCategories = activeCategories
      .filter((c) => !c.teamId && !c.departmentIds)
      .map((c) => c.title);

  const teamCategories = activeCategories
    .filter((c) => c.teamId && teamIds.includes(c.teamId))
    .map((c) => c.title);

  if (isWildcardScope) {
    return withForcedHubTitles([
      ...new Set([...unrestrictedCategories, ...departmentCategories, ...teamCategories]),
    ]);
  }

  if (!departments.length) {
    const allowed = [...new Set([...unrestrictedCategories, ...teamCategories])].filter((c) => c !== 'Dept Only');
    return withForcedHubTitles(allowed);
  }

  const allowed = [...new Set([...teamCategories, ...departmentCategories])];
  return withForcedHubTitles(allowed);
};

/**
 * Head ward nurses open `/report` on the form Home tab.
 * `/report?category=nurse_notes` (and ethic or SIREN) must stay on Dashboard.
 */
export function shouldForceHeadWardNurseHomeTab(input: {
  isHeadWardNurse: boolean;
  isSupervisorNurseOrAudit: boolean;
  cardCount: number;
  openExportTabFromQuery: boolean;
  reportingCategoryFromQuery: string | null;
}): boolean {
  if (!input.isHeadWardNurse || input.isSupervisorNurseOrAudit) return false;
  if (input.cardCount < 1) return false;
  if (input.openExportTabFromQuery) return false;
  if (input.reportingCategoryFromQuery) return false;
  return true;
}
